import { UploadProps } from 'antd/lib/upload'
import { UploadFile } from 'antd/lib/upload/interface'
import Raven from 'raven-js'
import * as React from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import styled from 'styled-components'
import notification from 'web-makeup/dist/Notification'
import Progress from 'web-makeup/dist/Progress'
import Upload from 'web-makeup/dist/Upload'

const Icon = require('antd/lib/icon')

interface OwnProps {
  action?: string
  uploadPreset?: string
  message: string

  // TODO: Upgrade this type to `React.ReactNode` when
  // `CloudinaryImage` component is merged into `master`.
  initialImage?: string

  onStart?(file: UploadFile): void

  onSuccess(response: string, file: UploadFile): void

  onError(error: Error, response: Response, file: UploadFile): void
}

type Props = OwnProps & UploadProps & ReactIntl.InjectedIntlProps

const AddAvatarIcon = styled(Icon)`
  font-size: 1.5rem;

  @media all and (min-width: 40rem) {
    font-size: 2rem;
  }
`

const StyledUpload = styled(Upload)`
  .ant-upload {
    color: #757575;
  }

  .ant-upload.ant-upload-select-picture-card {
    width: 4rem;
    height: 4rem;

    @media all and (min-width: 40rem) {
      width: ${props => props.theme['avatar-size-lg']};
      height: ${props => props.theme['avatar-size-lg']};
    }
  }
`

const UploadMessage = styled.div`
  @media all and (max-width: 40rem) {
    display: none;
  }
`

const StyledProgress = styled(Progress as any)`
  width: 3rem;
  height: 3rem;

  /*
    Yes I know we shouldn't use !important here, however ant uses inline
    styles and the styled-components docs recommend it anyway
    https://www.styled-components.com/docs/faqs#how-can-i-override-inline-styles
  */
  /* stylelint-disable declaration-no-important  */
  & .ant-progress-inner[style] {
    width: 3rem !important;
    height: 3rem !important;
    font-size: 0.75rem !important;
  }

  @media all and (min-width: 40rem) {
    width: 5.75rem;
    height: 5.75rem;

    & .ant-progress-inner[style] {
      width: 5.75rem !important;
      height: 5.75rem !important;
      font-size: 1.5rem !important;
    }
  }
  /* stylelint-enable declaration-no-important  */
`

const UploadedImage = styled.img`
  max-height: 3.4rem;

  @media all and (min-width: 40rem) {
    max-height: ${props => `calc(${props.theme['avatar-size-lg']} - 0.5rem - 2px)`};
  }
`

interface ProgressType {
  percent: number
}

interface Response {
  secure_url: string
  public_id: string
}

type State =
  | { screen: 'start' }
  | { screen: 'uploading'; percentUploaded: number }
  | { screen: 'success'; image: string }
  | { screen: 'error' }

class AvatarUpload extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      screen: 'start',
    }

    this.handleStart = this.handleStart.bind(this)
    this.handleProgress = this.handleProgress.bind(this)
    this.handleSuccess = this.handleSuccess.bind(this)
    this.handleError = this.handleError.bind(this)
  }

  handleStart(file: UploadFile) {
    const { onStart } = this.props

    this.setState({ screen: 'uploading', percentUploaded: 0 }, () => {
      if (typeof onStart === 'function') {
        onStart(file)
      }
    })
  }

  handleProgress(progress: ProgressType) {
    this.setState({ screen: 'uploading', percentUploaded: progress.percent })
  }

  handleSuccess(response: Response, file: UploadFile) {
    this.setState(
      {
        screen: 'success',
        image: response.secure_url,
      },
      () => {
        this.props.onSuccess(response.public_id, file)
      }
    )
  }

  handleError(error: Error, response: Response, file: UploadFile) {
    this.setState({ screen: 'error' }, () => {
      this.props.onError(error, response, file)
    })

    const { formatMessage } = this.props.intl

    notification.error({
      message: formatMessage({ id: 'avatar.upload.error.message' }),
      description: formatMessage({ id: 'avatar.upload.error.try_again' }),
      duration: 0,
    })

    Raven.captureException(error)
  }

  render() {
    const { initialImage } = this.props

    return (
      // @ts-ignore
      <StyledUpload
        action={this.props.action}
        name="avatar"
        listType="picture-card"
        showUploadList={false}
        data={file => ({ file, upload_preset: this.props.uploadPreset })}
        // @ts-ignore
        onStart={this.handleStart}
        onProgress={this.handleProgress}
        onSuccess={this.handleSuccess}
        onError={this.handleError}
      >
        {this.state.screen === 'start' &&
          (initialImage ? (
            <UploadedImage src={initialImage} />
          ) : (
            <React.Fragment>
              <AddAvatarIcon type="plus" />
              <UploadMessage>{this.props.message}</UploadMessage>
            </React.Fragment>
          ))}

        {this.state.screen === 'uploading' && (
          <React.Fragment>
            <StyledProgress type="circle" percent={Math.round(this.state.percentUploaded)} />
            <UploadMessage>
              <FormattedMessage id="avatar.upload.uploading_message" />
            </UploadMessage>
          </React.Fragment>
        )}

        {this.state.screen === 'success' && <UploadedImage src={this.state.image} />}
      </StyledUpload>
    )
  }
}

export default injectIntl(AvatarUpload)



// WEBPACK FOOTER //
// ./src/components/AvatarUpload/AvatarUpload.tsx